import { Lock, LockOpen } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { DataTable, type DataColumn } from '@/components/ui/DataTable'
import { EmptyState } from '@/components/ui/EmptyState'
import { Input, Label } from '@/components/ui/Input'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { SelectControl } from '@/components/ui/Select'
import { TableSkeleton } from '@/components/ui/Skeleton'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { fmtDateTime, fmtMoney } from '@/lib/format'

/** GET /accounting/periods → items. */
export interface ClosedPeriod {
  month: string
  label: string
  closed_by: number | null
  closed_at: string | null
  note: string | null
  profit_rub: string | null
  revenue_rub: string | null
}

export interface ClosedPeriodsPanelProps {
  /** undefined while loading */
  items?: ClosedPeriod[]
  /** the current Moscow month («2026-10-01»): it and later months cannot be closed */
  currentMonth: string
  canEdit: boolean
  busy?: boolean
  onClose: (month: string, note: string) => void
  onReopen: (p: ClosedPeriod) => void
}

const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь']

/** The 12 months before `current` («2026-10-01» → «2026-09», «2026-08», …). */
export function closableMonths(current: string, closed: readonly string[]): { value: string; label: string }[] {
  const [y, m] = current.split('-').map(Number)
  const out: { value: string; label: string }[] = []
  for (let back = 1; back <= 12; back++) {
    let mm = (m ?? 1) - back
    let yy = y ?? 2026
    while (mm <= 0) {
      mm += 12
      yy -= 1
    }
    const value = `${yy}-${String(mm).padStart(2, '0')}`
    if (!closed.includes(`${value}-01`)) out.push({ value, label: `${MONTHS[mm - 1]} ${yy}` })
  }
  return out
}

/**
 * «Закрытие периодов» (docs/accounting.md «Закрытие периода»): a closed month keeps the P&L stored
 * when it was closed, and refuses expenses, top-ups and tariffs dated into it (a closed month can still be distributed, whole, from its snapshot).
 * Corrections then land in the current month (storno). Only the owner closes and reopens; both go
 * to the audit log. The state is icon + word (rule 21).
 */
export function ClosedPeriodsPanel({ items, currentMonth, canEdit, busy = false, onClose, onReopen }: ClosedPeriodsPanelProps) {
  const options = closableMonths(currentMonth, (items ?? []).map((i) => i.month))
  const [month, setMonth] = useState('')
  const [note, setNote] = useState('')
  const chosen = month || options[0]?.value || ''
  const columns: DataColumn<ClosedPeriod>[] = [
    { id: 'month', header: 'Месяц', type: 'name', mobile: 'title', cell: (p) => <span className="first-letter:uppercase">{p.label}</span> },
    {
      id: 'status',
      header: 'Статус',
      type: 'status',
      mobile: 'status',
      cell: () => (
        <StatusBadge tone="neutral" icon={<Lock />}>
          Закрыт
        </StatusBadge>
      ),
    },
    { id: 'revenue', header: 'Выручка', type: 'money', unit: '₽', mobile: 'hidden', cell: (p) => fmtMoney(p.revenue_rub) },
    { id: 'profit', header: 'Прибыль', type: 'money', unit: '₽', mobile: 'amount', cell: (p) => fmtMoney(p.profit_rub) },
    { id: 'closed_at', header: 'Закрыт', type: 'date', mobile: 'meta', cell: (p) => fmtDateTime(p.closed_at) },
    { id: 'note', header: 'Комментарий', mobile: 'meta', cell: (p) => p.note ?? '—' },
    ...(canEdit
      ? [
          {
            id: 'actions',
            header: '',
            type: 'actions' as const,
            cell: (p: ClosedPeriod) => (
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => onReopen(p)}>
                <LockOpen className="size-4" aria-hidden />
                Открыть заново
              </Button>
            ),
          },
        ]
      : []),
  ]
  return (
    <div className="flex flex-col gap-4">
      <SectionHeader
        title="Закрытие периодов"
        description="Закрытый месяц не меняется задним числом: его отчёт — снимок на момент закрытия, записи с датой в нём не принимаются. Распределить закрытый месяц можно — целиком, по снимку."
      />
      {canEdit ? (
        <Card>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full sm:w-56">
              <Label htmlFor="closing-month">Месяц</Label>
              <SelectControl
                id="closing-month"
                value={chosen}
                disabled={options.length === 0}
                onChange={setMonth}
                options={options.length ? options : [{ value: '', label: 'Все прошедшие месяцы закрыты' }]}
              />
            </div>
            <div className="min-w-64 flex-1">
              <Label htmlFor="closing-note">Комментарий</Label>
              <Input id="closing-note" value={note} placeholder="например: сверено с выпиской" onChange={(e) => setNote(e.target.value)} />
            </div>
            <Button
              variant="primary"
              loading={busy}
              disabled={!chosen}
              onClick={() => {
                onClose(chosen, note)
                setNote('')
                setMonth('')
              }}
            >
              <Lock className="size-4" aria-hidden />
              Закрыть месяц
            </Button>
          </div>
          <p className="mt-2 text-caption text-ink-3">
            Текущий месяц закрыть нельзя. Поздний факт комиссии провайдера по платежу закрытого месяца в его отчёт не
            попадает — разница видна в сводке как «по живым данным».
          </p>
        </Card>
      ) : (
        <StatusBadge tone="neutral">Закрывает и открывает месяцы только владелец</StatusBadge>
      )}
      {items === undefined ? (
        <TableSkeleton cols={5} />
      ) : items.length === 0 ? (
        <EmptyState title="Закрытых месяцев нет" text="Закройте прошедший месяц, когда сверите его с выписками." />
      ) : (
        <DataTable label="Закрытые месяцы" rows={items} columns={columns} getRowId={(p) => p.month} />
      )}
    </div>
  )
}
